'use client';

import { useEffect, useRef } from 'react';
import { type ClerkRefreshTrigger, observeClerkRefreshHardReload, observeClerkRefreshStreak } from '@lib/observability/clerk-refresh-tags/observe-clerk-refresh.client';
import { refreshClerkSession } from '../clerk-session-bridge';
const RELOAD_AFTER_CONSECUTIVE_FAILURES = 3;

/**
 * Re-mints the Clerk JWT on browser-lifecycle events that can leave the
 * `__session` cookie stale:
 *
 * - `visibilitychange → visible` — primary case. Chrome 88+ clamps qualifying
 *   chained timers to once-per-minute after a tab is hidden > 5 min (chain
 *   count ≥ 5, silent ≥ 30 s, no WebRTC), so Clerk's 50 s main-thread refresh
 *   timer can miss the 60 s JWT lifetime. Server-side navigation under a
 *   stale cookie hits `clerkMiddleware` and 401-redirects before the reactive
 *   TanStack retry can recover.
 * - `pageshow` with `persisted: true` — bfcache restore. The page literally
 *   resumed from a snapshot taken before sleep; any cookie age is whatever it
 *   was at suspend time and `visibilitychange` does not fire on bfcache
 *   restore in Safari / Firefox. Without this listener the user can return
 *   from a 6 h sleep on iOS Safari and see the same hung navigation that the
 *   visibility-handler shipped to fix on Chrome.
 * - `online` — coming back from offline is a strong hint that whichever
 *   request was in flight when the network dropped (incl. Clerk's own
 *   refresh) may have stranded a stale cookie. Cheap to refresh on
 *   reconnect; debounced by the in-flight ref below. Offline duration is
 *   passed through as `hiddenDurationMs` so hang-log / caller bounds can
 *   scale the same way as a long-hidden wake (PORTAL-1AA).
 *
 * Three consecutive non-success outcomes (timed-out or failed) trigger a
 * hard reload, guarded by `visibilityState === 'visible'` so a backgrounded
 * tab does not reload itself. An in-flight ref guards against rapid event
 * cascades (e.g. visible → online firing back-to-back after sleep+reconnect)
 * spawning multiple handlers that would all await the same deduped
 * `refreshClerkSession` promise and each count the same outcome.
 *
 * Docs:
 *   https://developer.chrome.com/blog/timer-throttling-in-chrome-88
 *   https://clerk.com/docs/guides/sessions/force-token-refresh
 *   https://developer.mozilla.org/en-US/docs/Web/API/Window/pageshow_event
 */
export function useClerkVisibilityRefresh(): void {
  const consecutiveFailuresRef = useRef(0);
  const refreshInFlightRef = useRef(false);
  // Timestamp of the most recent `visibilitychange → hidden`. Used to
  // compute `tab.hidden_duration_ms` on the next visible / pageshow.
  // Null until the tab has been hidden at least once — the very first
  // visibility cycle after page load has no meaningful "hidden duration"
  // to report.
  const hiddenSinceRef = useRef<number | null>(null);
  // Timestamp of the most recent `offline` event. Used so `online`
  // refreshes can report how long the browser was disconnected and scale
  // hang logging (PORTAL-1AA). Null until we have observed an offline.
  const offlineSinceRef = useRef<number | null>(null);
  useEffect(() => {
    function computeHiddenDurationMs(): number | undefined {
      const since = hiddenSinceRef.current;
      if (since === null) return undefined;
      const duration = Date.now() - since;
      hiddenSinceRef.current = null;
      return duration < 0 ? undefined : duration;
    }
    function computeOfflineDurationMs(): number | undefined {
      const since_0 = offlineSinceRef.current;
      if (since_0 === null) return undefined;
      const duration_0 = Date.now() - since_0;
      offlineSinceRef.current = null;
      return duration_0 < 0 ? undefined : duration_0;
    }
    async function attemptRefreshAndMaybeReload(trigger: ClerkRefreshTrigger, hiddenDurationMs: number | undefined): Promise<void> {
      // In-flight guard: rapid event cascades (visible → online, or two
      // visible cycles in quick succession) fire this again while the
      // first invocation is still awaiting refreshClerkSession. The bridge
      // dedupes those callers to a single FAPI round-trip, so without this
      // guard every queued handler would resume on the same shared outcome
      // and increment consecutiveFailuresRef, tripping the reload threshold
      // on what is really one failure.
      if (refreshInFlightRef.current) return;
      refreshInFlightRef.current = true;
      try {
        const outcome = await refreshClerkSession({
          trigger,
          hiddenDurationMs
        });

        // The await above can take up to the caller-bound timeout (see
        // `CLERK_SESSION_REFRESH_TIMING`).
        // user hid the tab during that wait the outcome is not
        // user-perceived: skip both the failure increment and the success
        // reset so the streak only reflects refreshes the user actually
        // experienced while looking at the page.
        if (document.visibilityState !== 'visible') return;
        if (outcome.status === 'refreshed') {
          consecutiveFailuresRef.current = 0;
          observeClerkRefreshStreak(consecutiveFailuresRef.current);
          return;
        }
        // Signed-out and offline are not refresh-infra failures — do not
        // advance the hard-reload streak (PORTAL-K9 / PORTAL-178).
        if (outcome.status === 'no-session' || outcome.status === 'offline') {
          return;
        }
        consecutiveFailuresRef.current += 1;
        observeClerkRefreshStreak(consecutiveFailuresRef.current);
        if (consecutiveFailuresRef.current >= RELOAD_AFTER_CONSECUTIVE_FAILURES && document.visibilityState === 'visible') {
          // Capture the streak that tripped the reload BEFORE we reset
          // it, so the Sentry event carries the real threshold value.
          observeClerkRefreshHardReload({
            consecutiveFailures: consecutiveFailuresRef.current,
            trigger
          });
          consecutiveFailuresRef.current = 0;
          window.location.reload();
        }
      } finally {
        refreshInFlightRef.current = false;
      }
    }
    function handleVisibilityChange(): void {
      if (document.visibilityState === 'hidden') {
        // Stamp when the tab went background so the NEXT visible can
        // report how long the user was away. Overwrites any previous
        // value: only the latest hidden cycle is meaningful.
        hiddenSinceRef.current = Date.now();
        return;
      }
      if (document.visibilityState !== 'visible') return;
      void attemptRefreshAndMaybeReload('visibilitychange', computeHiddenDurationMs());
    }
    function handlePageshow(event: PageTransitionEvent): void {
      // `persisted: false` means a normal load (not bfcache) — Clerk's own
      // initial mint already runs at first paint, no extra refresh needed.
      if (!event.persisted) return;
      void attemptRefreshAndMaybeReload('pageshow', computeHiddenDurationMs());
    }
    function handleOffline(): void {
      offlineSinceRef.current = Date.now();
    }
    function handleOnline(): void {
      // Going online is a hint, not a guarantee that the cookie is stale.
      // Cheap to refresh: the bridge no-ops if there is no session, and the
      // in-flight ref dedupes against a concurrent visibility handler.
      // Pass offline duration so hang logging can scale (PORTAL-1AA); the
      // bridge also elevates hangLog for trigger=`online` itself.
      void attemptRefreshAndMaybeReload('online', computeOfflineDurationMs());
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageshow);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageshow);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);
}